import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Container,
  InputAdornment,
  IconButton,
  Tooltip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [verificationSent, setVerificationSent] = useState(false);
  const passwordInputRef = useRef(null);
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    if (window.__SESSION_EXPIRED__) {
      setSessionExpired(true);
      window.__SESSION_EXPIRED__ = false;
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error and verification status when user types
    setError('');
    setVerificationSent(false);
  };

  const handleClickShowPassword = () => {
    const input = passwordInputRef.current;
    const cursorPosition = input?.selectionStart || 0;
    setShowPassword(!showPassword);
    // Use setTimeout to ensure the cursor is set after the input type changes
    setTimeout(() => {
      if (input) {
        input.setSelectionRange(cursorPosition, cursorPosition);
      }
    }, 0);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  // Remove direct navigate after login
  // Add useEffect to navigate after user context is updated
  useEffect(() => {
    if (user) {
      if (!user.emailVerified) {
        navigate('/verify-email');
      } else if (user.isFirstLogin) {
        navigate('/change-password');
      } else if (user.termsAccepted === false) {
        navigate('/terms-acceptance');
      } else if (user.isSuperAdmin) {
        navigate('/super-admin');
      } else {
        navigate('/app/dashboard');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setVerificationSent(false);
    setLoading(true);

    try {
      await login(formData.email, formData.password);
      // navigation is now handled in useEffect
    } catch (err) {
      const status = err.response?.status;
      const data = err.response?.data;

      if (status === 429) {
        // Rate limiting error
        setError('Too many login attempts. Please wait a few minutes before trying again.');
      } else if (status === 403 && data?.code === 'BUSINESS_DEACTIVATED') {
        // Business deactivated error
        setError(data?.message || 'Your business account has been deactivated. Please contact support at support@kaziflex.com for assistance.');
      } else if (status === 403 && data?.message?.includes('verify')) {
        setVerificationSent(true);
        setError(data?.message || 'Please verify your email');
      } else if (status === 403) {
        // Other 403 errors (e.g., account not active)
        setError(data?.message || 'Access denied. Please contact support.');
      } else if (status === 401) {
        setError('Invalid email or password. Please check your credentials.');
      } else if (status === 500) {
        setError('Server error. Please try again later.');
      } else {
        // Handle cases where data might be a string (like rate limit message)
        const message = typeof data === 'string' ? data : data?.message;
        setError(message || 'Failed to login. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        backgroundColor: 'background.default',
        py: 12,
        px: 2,
      }}
    >
      <Container maxWidth="sm">
        {/* Show session expired message if needed */}
        {sessionExpired && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Your session has expired. Please log in again.
          </Alert>
        )}
        <Card
          elevation={0}
          sx={{
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
          }}
        >
          <CardContent
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              p: 4,
            }}
          >
            <Typography
              component="h1"
              variant="h3"
              gutterBottom
              sx={{
                color: 'primary.main',
                fontWeight: 600,
                mb: 1,
              }}
            >
              FlexPoS
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'text.secondary',
                fontWeight: 400,
                mb: 3,
              }}
            >
              Sign in to your account
            </Typography>

            {error && (
              <Alert 
                severity={verificationSent ? "info" : "error"} 
                sx={{ 
                  width: '100%', 
                  mb: 3,
                  '& .MuiAlert-message': {
                    width: '100%'
                  }
                }}
              >
                <Box>
                  {error}
                  {verificationSent && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      A new verification link has been sent to your email.
                      Please check your inbox and spam folder.
                    </Typography>
                  )}
                </Box>
              </Alert>
            )}

            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <TextField
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                placeholder="Enter your email address"
                autoComplete="email"
                autoFocus
                value={formData.email}
                onChange={handleChange}
                error={Boolean(error)}
              />

              <TextField
                required
                fullWidth
                name="password"
                label="Password"
                placeholder="Enter your password"
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="current-password"
                value={formData.password}
                onChange={handleChange}
                error={Boolean(error)}
                inputRef={passwordInputRef}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Show/Hide Password">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Sign In'}
              </Button>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mt: 2 }}>
                <Button
                  variant="text"
                  onClick={() => navigate('/register')}
                >
                  Don't have an account? Sign Up
                </Button>
                <Button
                  variant="text"
                  onClick={() => navigate('/request-password-reset')}
                >
                  Forgot Password?
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default Login;